import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Copy, Trash2, Eye, ExternalLink, ShieldCheck } from "lucide-react";
import { usePortfolio } from "@/context/PortfolioContext";
import { useAuth } from "@/context/AuthContext";

export default function ShareDialog({ open, onOpenChange }) {
  const { activeId } = usePortfolio();
  const { user } = useAuth();
  const userFirstName = (user?.name || "").trim().split(/\s+/)[0] || "Investor";
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");

  const publicUrl = share?.share_id ? `${window.location.origin}/api/p/${share.share_id}` : "";

  useEffect(() => {
    if (!open || !activeId) return;
    let cancelled = false;
    const run = async () => {
      // microtask defer so the lint rule does not see a synchronous setState in effect
      await Promise.resolve();
      if (cancelled) return;
      setLoading(true);
      try {
        const r = await api.get("/portfolio/share");
        if (cancelled) return;
        setShare(r.data);
        if (r.data?.display_name) {
          setName(r.data.display_name);
        } else {
          // No link yet — pre-fill the input with the user's first name as a sensible default
          setName(userFirstName);
        }
      } catch {
        if (cancelled) return;
        setShare({ enabled: false });
        setName(userFirstName);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, activeId]);

  const enable = async () => {
    setSaving(true);
    try {
      const r = await api.post("/portfolio/share", {
        display_name: name || undefined,
        show_quantities: true,
        show_avg_cost: false,
      });
      setShare(r.data);
      setName(r.data.display_name || "");
      toast.success("Share link created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link");
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = async (key, value) => {
    if (!share?.share_id) return;
    const next = { ...share, [key]: value };
    setShare(next);
    try {
      await api.put(`/portfolio/share/${share.share_id}`, { [key]: value });
    } catch {
      toast.error("Could not update setting");
      // Revert UI
      setShare(share);
    }
  };

  const renameShare = async () => {
    if (!share?.share_id) return;
    const trimmed = (name || "").trim();
    if (!trimmed) {
      toast.error("Display name cannot be empty");
      return;
    }
    if (trimmed === share.display_name) return;
    try {
      const r = await api.put(`/portfolio/share/${share.share_id}`, { display_name: trimmed });
      setShare((s) => ({ ...s, display_name: r.data?.display_name || trimmed }));
      toast.success("Display name updated");
    } catch {
      toast.error("Could not update name");
    }
  };

  const revoke = async () => {
    if (!share?.share_id) return;
    if (!window.confirm("Revoke this share link? Anyone with the URL will lose access.")) return;
    setSaving(true);
    try {
      await api.delete(`/portfolio/share/${share.share_id}`);
      setShare({ enabled: false });
      toast.success("Share link revoked");
    } catch {
      toast.error("Could not revoke share link");
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied to clipboard");
    } catch {
      // Fallback for older browsers / iOS Safari without permission
      const el = document.createElement("input");
      el.value = publicUrl;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand("copy"); toast.success("Link copied"); } catch { toast.error("Copy failed — long-press the link"); }
      el.remove();
    }
  };

  const enabled = !!(share && share.enabled);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-md max-h-[90vh] overflow-y-auto" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[21px] font-semibold tracking-[-0.015em]">
            Share portfolio
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
          A read-only link to your headline numbers, allocation, and holdings.
          Your transactions and personal info are never exposed.
        </p>

        {loading ? (
          <div className="py-10 text-center label-overline">Loading…</div>
        ) : !enabled ? (
          <div className="mt-5 space-y-4">
            <div>
              <Label className="label-overline mb-2 block">Display name (shown publicly)</Label>
              <Input
                data-testid="share-display-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Arjun, or Investor 042"
                className="rounded-lg"
                maxLength={60}
              />
            </div>
            <Button
              data-testid="share-enable-button"
              onClick={enable}
              disabled={saving}
              className="w-full rounded-xl"
              style={{ background: "var(--accent-primary)" }}
            >
              {saving ? "Creating link…" : "Create share link"}
            </Button>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            {/* URL display */}
            <div>
              <Label className="label-overline mb-2 block">Public link</Label>
              <div className="flex items-stretch gap-2">
                <Input
                  data-testid="share-url-input"
                  readOnly
                  value={publicUrl}
                  onClick={(e) => e.target.select()}
                  className="rounded-lg font-mono text-xs flex-1"
                />
                <Button
                  data-testid="share-copy-button"
                  onClick={copyUrl}
                  variant="outline"
                  className="rounded-lg shrink-0"
                  aria-label="Copy link"
                >
                  <Copy className="w-4 h-4" strokeWidth={1.75} />
                </Button>
              </div>
              <p className="text-[11px] mt-1.5" style={{ color: "var(--text-muted)" }}>
                Paste in WhatsApp, X, Slack or LinkedIn — a preview card appears automatically. Opens the live page on click.
              </p>
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="share-preview-link"
                className="inline-flex items-center gap-1 text-xs mt-2"
                style={{ color: "var(--accent-primary)" }}
              >
                <ExternalLink className="w-3 h-3" strokeWidth={2} /> Preview as visitor
              </a>
            </div>

            {/* Display name (editable) */}
            <div>
              <Label className="label-overline mb-2 block">Display name</Label>
              <div className="flex items-stretch gap-2">
                <Input
                  data-testid="share-name-edit"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="rounded-lg flex-1"
                  maxLength={60}
                />
                <Button
                  data-testid="share-name-save"
                  onClick={renameShare}
                  variant="outline"
                  className="rounded-lg shrink-0"
                  disabled={!name.trim() || name.trim() === share.display_name}
                >
                  Save
                </Button>
              </div>
            </div>

            {/* Privacy toggles */}
            <div className="rounded-xl p-4" style={{ background: "var(--bg-base)" }}>
              <div className="flex items-start gap-2 mb-3">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={2} style={{ color: "var(--accent-primary)" }} />
                <p className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                  Tune what visitors see. Returns &amp; the market-cap mix are always shown.
                </p>
              </div>
              <ToggleRow
                label="Show holdings list"
                hint="The full table of symbols & per-stock values (incl. the allocation pie)"
                checked={share.show_holdings !== false}
                onChange={(v) => updateSetting("show_holdings", v)}
                testid="toggle-show-holdings"
              />
              <ToggleRow
                label="Show quantities"
                hint="Number of shares for each holding"
                checked={!!share.show_quantities}
                onChange={(v) => updateSetting("show_quantities", v)}
                testid="toggle-show-quantities"
              />
              <ToggleRow
                label="Show average cost"
                hint="Per-share cost basis (reveals buying strategy)"
                checked={!!share.show_avg_cost}
                onChange={(v) => updateSetting("show_avg_cost", v)}
                testid="toggle-show-avg-cost"
              />
            </div>

            {/* Stats */}
            <div className="flex items-center justify-between text-[11px]" style={{ color: "var(--text-muted)" }}>
              <span className="inline-flex items-center gap-1.5">
                <Eye className="w-3 h-3" strokeWidth={2} />
                {share.view_count || 0} view{(share.view_count || 0) === 1 ? "" : "s"}
              </span>
              {share.last_viewed_at && (
                <span>Last seen {new Date(share.last_viewed_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
              )}
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button
                data-testid="share-revoke-button"
                onClick={revoke}
                disabled={saving}
                variant="outline"
                className="rounded-xl"
                style={{ color: "var(--accent-negative)", borderColor: "var(--accent-negative)" }}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />
                Revoke link
              </Button>
              <Button onClick={() => onOpenChange(false)} className="rounded" style={{ background: "var(--btn-primary-bg)" }}>
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0">
        <p className="text-sm font-medium leading-tight">{label}</p>
        <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>{hint}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
